import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { 
  DataGridPro, 
  GridColDef, 
  GridColumnVisibilityModel, 
  GridEventListener, 
  GridRenderCellParams, 
  GridRowId, 
  GridSortModel, 
} from '@mui/x-data-grid-pro';
import FormField from 'components/FormField'
import { isEmpty } from 'lodash';
import { useAPI } from 'contexts/APIProvider'
import { PracticeGroupSearchForm } from 'types/interfaces'
import PracticeGroup from 'logic/PracticeGroup'
import { Observable } from 'rxjs';
import { Chip } from '@mui/material';
import { useNavigate } from 'react-router';
import { adminBillingAccountDetailURL } from 'routes/urls';

interface AdminPracticeGroupTableProps {
  reload$?: Observable<boolean>
  onRowClick?: GridEventListener<"rowClick">
  actions?: React.ReactNode
  form: PracticeGroupSearchForm
  updateForm: (name: string, value: any) => void
  selection: GridRowId[]
  setSelection: (value: GridRowId[]) => void
  columnVisibilityModel: GridColumnVisibilityModel
  setColumnVisibilityModel: (value: GridColumnVisibilityModel) => void
  sortModel: GridSortModel
  setSortModel: (value: GridSortModel) => void
}

const AdminPracticeGroupTable: React.FC<AdminPracticeGroupTableProps> = ({
  reload$,
  onRowClick,
  actions,
  form,
  updateForm,
  selection,
  setSelection,
  columnVisibilityModel,
  setColumnVisibilityModel,
  sortModel,
  setSortModel,
}) => {
  const { api } = useAPI()
  const navigate = useNavigate()
  const [groups, setGroups] = React.useState<PracticeGroup[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const fetchPracticeGroups = React.useCallback(async () => {
    setLoading(true)
    setGroups(await api.listPracticeGroup(form))
    setLoading(false)
  }, [api, form])

  const onSelectionChanged = React.useCallback((selectedIDs: GridRowId[]) => {
    setSelection(selectedIDs)
  }, [setSelection])

  React.useEffect(() => {
    fetchPracticeGroups()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const sub = reload$?.subscribe(() => fetchPracticeGroups())
    return () => sub?.unsubscribe()
  }, [fetchPracticeGroups, reload$])

  const columns: GridColDef<PracticeGroup>[] = React.useMemo(() => [{
    minWidth: 50,
    headerName: 'ID',
    field: 'id',
    type: 'number',
  }, {
    minWidth: 200,
    headerName: 'Name',
    field: 'name',
    type: 'string',
  }, {
    minWidth: 200,
    headerName: 'Billing Accounts',
    field: 'billing_accounts',
    renderCell: (params: GridRenderCellParams<PracticeGroup>) => {
      return params.row.billing_accounts.map((account) => (
        <Chip key={account.id} label={account.name} size='small' onClick={() => navigate(adminBillingAccountDetailURL(account.id))} />
      ))
    }
  }], [navigate])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <FormField
          name='name'
          label='Practice Group Name'
          type='text'
          onChange={(e) => updateForm('name', e.target.value)}
          value={form.name}
        />
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {actions}
        </Box>
      </Box>
      {loading ? (
        <Alert severity='info'>Please Wait ...</Alert>
      ) : isEmpty(groups) ? (
        <Alert severity='info'>No Records Yet</Alert>
      ) : (
        <React.Fragment>
          <Alert severity='info'>{groups.length} records returned</Alert>
          <DataGridPro 
            rows={groups}
            columns={columns}
            getRowId={(group) => group.id}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selection}
            onRowSelectionModelChange={onSelectionChanged}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            sortModel={sortModel}
            onSortModelChange={(newModel) => setSortModel(newModel)}
            loading={loading}
            onRowDoubleClick={onRowClick}
            hideFooter
          />
        </React.Fragment>
      )}
    </Box>
  )
}

export default AdminPracticeGroupTable